import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
    Flex,
    IconError,
    Spacer,
} from '@zillow/constellation';

import { PHONE_VERIFICATION_STEPS } from 'app/shared/constants/WizardStepsConstants';
import MaxAttemptsError from 'app/shared/components/Wizard/PhoneVerificationWizard/components/MaxAttemptsError';
import numberUtils from 'app/shared/utils/numberUtils';
import PostingActions from 'app/shared/actions/PostingActions';
import VerificationCodeValidator from 'app/shared/components/VerificationCodeValidator';
import PhoneVerificationBodyTemplate from '../PhoneVerificationBodyTemplate';

import {
    StyledCTALabel,
    StyledCTAWrapper,
    StyledEditButton,
    StyledErrorText,
    StyledFullWidthButton,
    StyledText,
} from '../../StyledComponents';

const propTypes = {
    contactPhone: PropTypes.string.isRequired,
    currentStepIndex: PropTypes.number.isRequired,
    incrementStep: PropTypes.func.isRequired,
    isNumberEditable: PropTypes.bool,
    verificationType: PropTypes.string.isRequired,
    onVerificationConfirmation: PropTypes.func, // eslint-disable-line react/require-default-props
};

const defaultProps = {
    isNumberEditable: true,
};

const VerificationCodeSent = ({
    contactPhone,
    currentStepIndex,
    incrementStep,
    isNumberEditable,
    onVerificationConfirmation,
    verificationType,
}) => {
    const dispatch = useDispatch();
    const [error, setError] = useState();
    const [verificationCode, setVerificationCode] = useState('');

    const confirmVerification = () => {
        const contactObj = {
            phone: contactPhone,
            contactType: verificationType,
            verificationCode,
        };

        if (verificationCode.length < 5) {
            setError('Please enter a 5 digit verification code.');
            return;
        }

        dispatch(PostingActions.property.confirmVerification(contactObj))
            .then((result) => {
                if (result.success) {
                    if (onVerificationConfirmation) {
                        onVerificationConfirmation(contactPhone)
                            .then(() => incrementStep())
                            .catch(err => setError(err));
                    } else {
                        incrementStep();
                    }
                } else {
                    setError(result.error.message);
                }
            });
    };

    const renderErrorMessage = () => {
        const message = error === 'Invalid verification code' ?
            'The code you entered is incorrect.' :
            'There was an error verifying your code.';

        if (error === 'Too many attempts') {
            return <MaxAttemptsError />;
        }

        return (
            <Spacer marginTop="sm">
                <Flex display="flex" alignItems="center" justifyContent="center">
                    <IconError fontColor="red600" size="sm" marginRight="xs" />
                    <StyledErrorText as="div">
                        {`${message} Try again.`}
                    </StyledErrorText>
                </Flex>
            </Spacer>
        );
    };

    return (
        <PhoneVerificationBodyTemplate
            heading={`Step ${currentStepIndex + 1} - Verification code sent`}
            subheading={(
                <StyledText>
                    We sent the 5 digit code to
                    {` ${numberUtils.formatPhoneNumberInput(contactPhone)}. `}
                    {isNumberEditable && (
                        <StyledEditButton onClick={() => incrementStep(PHONE_VERIFICATION_STEPS.ADD_PHONE_NUMBER)}>
                            Edit number
                        </StyledEditButton>
                    )}
                </StyledText>
            )}
        >
            <Flex display="flex" alignItems="center" flexDirection="column">
                <StyledCTAWrapper>
                    <StyledCTALabel>Enter the verification code below.</StyledCTALabel>
                </StyledCTAWrapper>
                <VerificationCodeValidator
                    codeLength={5}
                    onComplete={setVerificationCode}
                    clearErrors={setError}
                    onEnterKeyPress={confirmVerification}
                />
                <Spacer marginTop="sm" marginBottom="md">
                    <StyledText fontType="finePrint">
                        Didn&#39;t receive the code?&nbsp;
                        <StyledEditButton onClick={() => incrementStep(PHONE_VERIFICATION_STEPS.GET_VERIFICATION_CODE)}>
                            Get new code
                        </StyledEditButton>
                    </StyledText>
                </Spacer>
                <StyledFullWidthButton onClick={confirmVerification} buttonType="primary">
                        Check code
                </StyledFullWidthButton>
            </Flex>
            {error && (
                <Spacer marginY="xs">
                    {renderErrorMessage()}
                </Spacer>
            )}
        </PhoneVerificationBodyTemplate>
    );
};

VerificationCodeSent.propTypes = propTypes;
VerificationCodeSent.defaultProps = defaultProps;

export default VerificationCodeSent;
